import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Ticket, Wallet, History } from "lucide-react";
import { api, inr, fmtDateTime, apiError } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/common";
import { CoinIcon } from "@/components/CoinBadge";
import { useT } from "@/context/i18n";

export default function Rewards() {
  const [d, setD] = useState(null);
  const t = useT();
  const load = () => api.get("/rewards").then((r) => setD(r.data)).catch(() => setD({ points: 0, cashback: 0, reward_rupees: 0, transactions: [], coupons: [] }));
  useEffect(() => { load(); }, []);
  const redeem = async (c) => {
    try { const { data } = await api.post(`/rewards/redeem/${c.id}`); toast.success(`${data.message}. Code: ${data.code}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <div className="space-y-4"><Skeleton className="h-32 w-full" /><div className="grid gap-4 sm:grid-cols-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40" />)}</div></div>;
  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{t("Rewards")}</h1><p className="text-sm text-slate-500">Earn ZRo Pe Coins on on-time EMIs and games, then redeem curated coupons.</p></div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl navy-grain p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-300"><CoinIcon size={16} /> ZRo Pe Coins</div>
              <p className="mt-2 font-display text-3xl font-bold">{d.points.toLocaleString("en-IN")}</p>
              <p className="mt-1 text-[11px] text-slate-400" data-testid="coins-nonwithdrawable">Non-withdrawable — use on eligible payments.</p>
            </div>
            <img src="/brand/zrope-coin.png" alt="ZRo Pe Coins" className="h-16 w-16 rounded-full object-cover" />
          </div>
        </div>
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5" data-testid="reward-cash-card">
          <div className="flex items-center gap-2 text-sm text-emerald-700"><Gift size={16} /> Direct Reward (Reward Cash)</div>
          <p className="mt-2 font-display text-3xl font-bold text-[#0B132B]">{inr(d.reward_rupees || 0)}</p>
          <p className="text-[11px] text-slate-500" data-testid="reward-cash-nonwithdrawable">Non-withdrawable reward — can only be used toward eligible payments.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-center gap-2 text-sm text-slate-500"><Wallet size={16} className="text-[#14B8A6]" /> {t("Cashback")}</div><p className="mt-2 font-display text-3xl font-bold text-[#0B132B]">{inr(d.cashback)}</p><p className="text-xs text-slate-400">{t("Reward-system credit")}</p></div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-center gap-2 text-sm text-slate-500"><Ticket size={16} className="text-[#14B8A6]" /> {t("Available Coupons")}</div><p className="mt-2 font-display text-3xl font-bold text-[#0B132B]">{d.coupons.length}</p></div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-[#0B132B]">{t("Redeemable Rewards")}</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {d.coupons.map((c) => (
            <div key={c.id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-emerald-50 text-[#14B8A6]"><Ticket size={20} /></span>
              <p className="mt-3 font-semibold text-[#0B132B]">{c.title}</p>
              <p className="text-xs text-slate-400">Expires {c.expiry}</p>
              <p className="mt-2 text-sm font-bold text-[#14B8A6]">{c.cost_points.toLocaleString("en-IN")} Coins</p>
              <button onClick={() => redeem(c)} disabled={d.points < c.cost_points} data-testid={`redeem-${c.id}`}
                className="mt-3 rounded-md bg-[#14B8A6] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0D9488] disabled:bg-slate-200 disabled:text-slate-400">
                {d.points < c.cost_points ? "Not enough Coins" : t("Redeem")}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0B132B]"><History size={18} /> Voucher &amp; Coins History</h2>
        <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm">
          {d.transactions.length === 0 ? <EmptyState icon={Gift} mascot title={t("No reward activity yet")} desc="Pay an EMI on time or play a game to start earning ZRo Pe Coins." /> : d.transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 text-sm last:border-0">
              <div><p className="font-medium text-[#0B132B]">{String(t.event_type ?? "activity").replace(/_/g, " ")}</p><p className="text-xs text-slate-400">{fmtDateTime(t.timestamp)} • {t.source}</p></div>
              <span className={`font-semibold ${t.points >= 0 ? "text-[#14B8A6]" : "text-red-500"}`}>{t.points >= 0 ? "+" : ""}{t.points} Coins</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
